/**
 * QuickCreateMenu — always-visible "+ Create" button in the top nav.
 *
 * Lists the platform's primary creation flows in one click-through menu so
 * users never have to hunt for them across tabs/sub-modules.
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, FileText, ShoppingCart, PackageCheck, Receipt,
  ClipboardList, Gavel, AlertTriangle, FileSignature,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';

const ITEMS = [
  { icon: ClipboardList, label: 'Purchase Requisition (PR)', hint: 'Start a new PR with AVL check', to: '/app/requisitions?tab=create', color: 'text-amber-600 bg-amber-50' },
  { icon: ShoppingCart,  label: 'Purchase Order (PO)',       hint: 'Manual PO or convert from PR', to: '/app/purchase-orders', color: 'text-sky-600 bg-sky-50' },
  { icon: PackageCheck,  label: 'Goods Receipt (GRN)',       hint: 'Record receipt against an open PO', to: '/app/grn', color: 'text-emerald-600 bg-emerald-50' },
  { icon: Receipt,       label: 'Invoice',                    hint: 'Capture invoice or submit from portal', to: '/app/invoices?tab=intake', color: 'text-violet-600 bg-violet-50' },
  { divider: true },
  { icon: FileSignature, label: 'Sourcing Event (RFQ/RFP/Auction)', hint: 'Launch a new sourcing event', to: '/app/sourcing/wizard', color: 'text-blue-600 bg-blue-50' },
  { icon: FileText,      label: 'Contract',                    hint: 'Author a new contract', to: '/app/cm/repository?action=new', color: 'text-indigo-600 bg-indigo-50' },
  { icon: AlertTriangle, label: 'Claim',                       hint: 'Raise a new claim/dispute', to: '/app/cld/register?action=new', color: 'text-rose-600 bg-rose-50' },
  { icon: Gavel,         label: 'Variation Order',             hint: 'Raise a contract variation', to: '/app/cm/variations?action=new', color: 'text-orange-600 bg-orange-50' },
];

export default function QuickCreateMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <div className="relative" ref={ref}>
      <Button
        onClick={() => setOpen(o => !o)}
        size="sm"
        className="h-8 px-3 bg-[#F26B1F] hover:bg-[#D45A14] text-white font-medium shadow-sm"
        data-testid="quick-create-btn"
        title="Create new record (PR / PO / GRN / Invoice / …)"
      >
        <Plus className="w-3.5 h-3.5 mr-1" />
        <span className="hidden md:inline">Create</span>
      </Button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden"
            data-testid="quick-create-menu"
          >
            <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-[#0F131C] to-[#1E4E8C]">
              <div className="text-[10px] font-mono uppercase tracking-wider text-blue-200/80">Quick Create</div>
              <div className="text-sm font-semibold text-white">What would you like to create?</div>
            </div>
            <div className="py-1 max-h-[420px] overflow-y-auto">
              {ITEMS.map((it, i) =>
                it.divider ? (
                  <div key={i} className="my-1 h-px bg-gray-100" />
                ) : (
                  <button
                    key={i}
                    onClick={() => go(it.to)}
                    className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition"
                    data-testid={`quick-create-${it.label.toLowerCase().split(' ')[0].replace(/[^a-z]/g, '')}`}
                  >
                    <div className={`w-8 h-8 rounded-md grid place-items-center flex-shrink-0 ${it.color}`}>
                      <it.icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">{it.label}</div>
                      <div className="text-[11px] text-gray-500 truncate">{it.hint}</div>
                    </div>
                  </button>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
